import * as React from 'react';
import { Flex, Icon } from '@chakra-ui/core';
import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Stack } from '../stack';

export type StatProps = BoxProps & {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    icon?: string;
    iconColor?: string;
    value?: string;
    valueSize?: string;
    label?: string;
    units?: string;
    caption?: string;
};

const configForSize = (size: string): any => {
    switch (size) {
    case 'sm':
        return {
            icon: {
                size: '24px',
                mr: 1
            },
            value: {
                fontSize: 'xl',
                mt: '3px'
            },
            label: {
                fontSize: '2xs'
            },
            units: {
                fontSize: 'xs'
            },
            caption: {
                fontSize: 'xs'
            }
        };
    case 'md':
        return {
            icon: {
                size: '30px',
                mr: 2
            },
            value: {
                fontSize: '2xl',
                mt: 1
            },
            label: {
                fontSize: 'xs'
            },
            units: {
                fontSize: 'sm'
            },
            caption: {
                fontSize: 'xs'
            }
        };
    case 'lg':
        return {
            icon: {
                size: '40px',
                mr: 2
            },
            value: {
                fontSize: '3xl',
                mt: 1,
                mb: -1
            },
            label: {
                fontSize: 'md'
            },
            units: {
                fontSize: 'lg'
            },
            caption: {
                fontSize: 'sm'
            }
        };
    case 'xl':
        return {
            icon: {
                size: '50px',
                mr: 3
            },
            value: {
                fontSize: '4xl',
                mb: -2
            },
            label: {
                fontSize: 'lg'
            },
            units: {
                fontSize: 'xl'
            },
            caption: {
                fontSize: 'md'
            }
        };
    default:
    }

    return null;
};

const Stat: React.FC<StatProps> = (props: StatProps): React.ReactElement => {
    const { size, icon, iconColor, value, label, units, caption, ...rest } = props;

    const opts = size ? configForSize(size) : {};
    delete rest.valueSize;

    return (
        <Flex {...rest}>
            {icon && <Icon color={iconColor} name={icon} {...opts.icon} />}
            <Box>
                <Stack align="baseline" spacing="3px" {...opts.value} isInline>
                    <Text
                        m={0}
                        color="text.base.primary"
                        fontFamily="heading"
                        fontWeight="medium"
                        fontSize={props.valueSize || opts.value.fontSize}
                    >
                        {value}
                    </Text>
                    {units && (
                        <Text m={0} fontWeight="400" color="text.base.secondary" {...opts.units}>
                            {units}
                        </Text>
                    )}
                </Stack>
                {label && (
                    <Text
                        m={0}
                        variant="label"
                        fontWeight="600"
                        fontSize={props.labelSize}
                        color="text.base.secondary"
                        {...opts.label}
                    >
                        {label}
                    </Text>
                )}
                {caption && (
                    <Text
                        mt={label ? 1 : 0}
                        mb={0}
                        fontSize={props.captionSize}
                        color="text.base.secondary"
                        {...opts.caption}
                    >
                        {caption}
                    </Text>
                )}
            </Box>
        </Flex>
    );
};

Stat.defaultProps = {
    size: 'md',
    iconColor: 'currentColor'
};

export default Stat;
